import type { ClubRole } from "@rivaled/shared";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  CreditCard,
  FileText,
  Gamepad2,
  HelpCircle,
  Link2,
  Loader2,
  Save,
  Send,
  Settings,
  Trash2,
  Users,
} from "lucide-react";
import { useState } from "react";
import { DiscordIcon } from "@/components/icons/BrandIcons";
import type {
  ClubData,
  ClubSettingsForm,
  NavItem,
  SectionId,
} from "../../../../components/club-settings";
import {
  ApplicationsSection,
  BillingSection,
  DangerSection,
  DiscordSection,
  GamesSection,
  GeneralSection,
  InvitationsSection,
  MembersSection,
  SocialLinksSection,
  sectionIds,
  useClubSettingsForm,
} from "../../../../components/club-settings";
import {
  Breadcrumbs,
  Button,
  Drawer,
  DrawerBody,
  DrawerHeader,
  SettingsSection,
} from "../../../../components/ui";
import { usePlatformRole } from "../../../../hooks/usePlatformRole";
import { canClubRolePermission } from "../../../../lib/permissions";
import { trpc, type RouterOutputs } from "../../../../lib/trpc";

// ---------------------------------------------------------------------------
// Route
// ---------------------------------------------------------------------------

export const Route = createFileRoute("/app/clubs/$slug/settings")({
  validateSearch: (search: Record<string, unknown>): { section: SectionId } => {
    const s = search.section;
    if (typeof s === "string" && (sectionIds as readonly string[]).includes(s)) {
      return { section: s as SectionId };
    }
    return { section: "general" };
  },
  component: ClubSettingsPage,
});

// ---------------------------------------------------------------------------
// Nav config
// ---------------------------------------------------------------------------

const navItems: NavItem[] = [
  { id: "general", label: "General", icon: Settings, adminOnly: true },
  { id: "social", label: "Social Links", icon: Link2, adminOnly: true },
  { id: "discord", label: "Discord", icon: DiscordIcon, adminOnly: true },
  { id: "invitations", label: "Invitations", icon: Send },
  { id: "games", label: "Games", icon: Gamepad2, adminOnly: true },
  { id: "members", label: "Members", icon: Users },
  { id: "applications", label: "Applications", icon: FileText },
  { id: "billing", label: "Billing", icon: CreditCard, ownerOnly: true },
  { id: "danger", label: "Delete", icon: Trash2, danger: true, ownerOnly: true },
];

// Sections that share form state — Save button only shows for these
const formSections = new Set<SectionId>(["general", "social"]);

// ---------------------------------------------------------------------------
// Section docs (help drawer)
// ---------------------------------------------------------------------------

const sectionDocs: Record<SectionId, React.ReactNode> = {
  general: (
    <div className="space-y-3 text-sm text-surface-300">
      <p>
        Set your club's display name, description, and privacy level. These are visible to anyone
        browsing or discovering your club.
      </p>
      <p>
        <span className="text-white font-medium">Public</span> clubs appear in search and anyone can
        join. <span className="text-white font-medium">Invite Only</span> clubs are visible but
        require an invite code. <span className="text-white font-medium">Private</span> clubs are
        hidden from discovery entirely.
      </p>
    </div>
  ),
  social: (
    <div className="space-y-3 text-sm text-surface-300">
      <p>
        Add links to your club's social media profiles. These are displayed on your club's public
        page so members and visitors can find you elsewhere.
      </p>
    </div>
  ),
  discord: (
    <div className="space-y-3 text-sm text-surface-300">
      <p>Link a Discord server to enable automatic match voice channels and notifications.</p>
      <p>
        The Rivaled bot needs <span className="text-white font-medium">Manage Channels</span>,{" "}
        <span className="text-white font-medium">Move Members</span>, and{" "}
        <span className="text-white font-medium">Connect</span> permissions to create and manage
        match channels.
      </p>
      <p>
        <span className="text-white font-medium">Moderator Roles</span> grant visibility to all
        match channels regardless of participation.
      </p>
    </div>
  ),
  invitations: (
    <div className="space-y-3 text-sm text-surface-300">
      <p>
        Create invite links with optional expiration and usage limits, or send direct invites to
        specific users by their ID.
      </p>
      <p>Invite links can be shared publicly. Each link tracks how many times it's been used.</p>
    </div>
  ),
  games: (
    <div className="space-y-3 text-sm text-surface-300">
      <p>
        Configure which games your club competes in. Each game can have a default team size that
        queues will inherit.
      </p>
    </div>
  ),
  members: (
    <div className="space-y-3 text-sm text-surface-300">
      <p>View and manage club members. Each role has different management capabilities:</p>
      <p>
        <span className="text-white font-medium">Owner</span> — full control including billing,
        deleting the club, and transferring ownership.{" "}
        <span className="text-white font-medium">Admin</span> — can manage all settings, change
        roles (mod/member), and kick anyone below admin.{" "}
        <span className="text-white font-medium">Moderator</span> — can kick members and review
        applications/invitations. <span className="text-white font-medium">Member</span> — no
        management access.
      </p>
    </div>
  ),
  applications: (
    <div className="space-y-3 text-sm text-surface-300">
      <p>
        When your club is set to <span className="text-white font-medium">Invite Only</span> or{" "}
        <span className="text-white font-medium">Private</span>, users can submit an application to
        request membership instead of joining directly.
      </p>
      <p>
        Applications appear here for review. Approve to add the user as a member, or reject to
        decline their request. Both admins and moderators can review applications.
      </p>
    </div>
  ),
  billing: (
    <div className="space-y-3 text-sm text-surface-300">
      <p>
        Manage your club's subscription plan, view invoices, and update payment details. The
        Competitive plan unlocks advanced features like custom matchmaking, restrictions, and more.
      </p>
    </div>
  ),
  danger: (
    <div className="space-y-3 text-sm text-surface-300">
      <p>
        Deleting a club permanently removes all data including members, matches, queues, stats, and
        configuration. This action cannot be undone.
      </p>
    </div>
  ),
};

const sectionTitles: Record<SectionId, string> = {
  general: "General",
  social: "Social Links",
  discord: "Discord Integration",
  invitations: "Invitations",
  games: "Games",
  members: "Members",
  applications: "Applications",
  billing: "Billing",
  danger: "Delete",
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function ClubSettingsPage() {
  const { slug } = Route.useParams();
  const { section } = Route.useSearch();
  const navigate = useNavigate();
  const {
    isAdmin: isPlatformAdmin,
    isLoading: isPlatformRoleLoading,
    isModerator: isPlatformModerator,
  } = usePlatformRole();

  const { data: club, isLoading } = trpc.club.get.useQuery({ slug });
  const { data: myClubs, isLoading: isMyClubsLoading } = trpc.club.getMyClubs.useQuery();
  const membership = myClubs?.find((c) => c.slug === slug);
  const role = membership?.role as ClubRole | undefined;
  const isOwner = role === "owner" || isPlatformAdmin;
  const isAdmin =
    canClubRolePermission(role, { resource: "club", actions: ["update"] }) || isPlatformModerator;
  const isModerator =
    canClubRolePermission(role, {
      resource: "dispute",
      actions: ["view"],
    }) || isPlatformModerator;
  const canManageInvites =
    canClubRolePermission(role, {
      resource: "member",
      actions: ["invite"],
    }) || isPlatformModerator;
  const hasSettingsAccess = isAdmin || isModerator || canManageInvites;

  const [helpSection, setHelpSection] = useState<SectionId | null>(null);

  if (isLoading || isMyClubsLoading || isPlatformRoleLoading) {
    return (
      <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-6">
        <div className="h-8 w-48 rounded-lg bg-surface-700/40 animate-pulse" />
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-32 rounded-xl bg-surface-800/50 animate-pulse" />
        ))}
      </div>
    );
  }

  if (!club || !hasSettingsAccess) {
    void navigate({ to: "/app/clubs/$slug", params: { slug } });
    return null;
  }

  return (
    <ClubSettingsPageInner
      club={club}
      slug={slug}
      section={section}
      isOwner={isOwner}
      myRole={membership?.role ?? "member"}
      helpSection={helpSection}
      setHelpSection={setHelpSection}
    />
  );
}

// Inner component — rendered only when club data is available, so the form
// hook is called unconditionally and shared between header + content.

function ClubSettingsPageInner({
  club,
  slug,
  section,
  isOwner,
  myRole,
  helpSection,
  setHelpSection,
}: {
  club: NonNullable<RouterOutputs["club"]["get"]>;
  slug: string;
  section: SectionId;
  isOwner: boolean;
  myRole: string;
  helpSection: SectionId | null;
  setHelpSection: (s: SectionId | null) => void;
}) {
  const navigate = useNavigate();
  const { isAdmin: isPlatformAdmin, isModerator: isPlatformModerator } = usePlatformRole();
  const isAdmin =
    canClubRolePermission(myRole as ClubRole, {
      resource: "club",
      actions: ["update"],
    }) || isPlatformModerator;
  const canManageInvites =
    canClubRolePermission(myRole as ClubRole, {
      resource: "member",
      actions: ["invite"],
    }) || isPlatformModerator;
  const form = useClubSettingsForm(club as ClubData, slug);
  const showSave = isAdmin && formSections.has(section);

  const setSection = (id: SectionId) => {
    void navigate({
      to: "/app/clubs/$slug/settings",
      params: { slug },
      search: { section: id },
      replace: true,
    });
  };

  const visibleNavItems = navItems.filter((item) => {
    if (item.ownerOnly && !isOwner) return false;
    if (item.adminOnly && !isAdmin) return false;
    return true;
  });

  // Redirect to first visible section if current section isn't accessible
  const canAccessSection = visibleNavItems.some((item) => item.id === section);
  if (!canAccessSection) {
    const fallback = visibleNavItems[0]?.id ?? "members";
    void navigate({
      to: "/app/clubs/$slug/settings",
      params: { slug },
      search: { section: fallback },
      replace: true,
    });
    return null;
  }

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-5">
      {/* Header */}
      <div className="space-y-3">
        <Breadcrumbs
          items={[
            { label: club.name, to: "/app/clubs/$slug", params: { slug } },
            { label: "Club Settings" },
          ]}
        />
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Club Settings</h1>
            <p className="text-sm text-surface-400">
              Manage your club's profile, members, and configuration.
            </p>
          </div>
          {showSave && (
            <Button
              variant="primary"
              size="md"
              leftIcon={
                form.isSaving ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Save className="size-4" />
                )
              }
              disabled={form.isSaving || form.hasErrors}
              onClick={form.handleSave}
            >
              {form.isSaving ? "Saving..." : "Save Changes"}
            </Button>
          )}
        </div>
      </div>

      {/* Mobile nav strip */}
      <div className="lg:hidden overflow-x-auto scrollbar-none -mx-4 px-4">
        <div className="flex gap-1 min-w-max pb-1">
          {visibleNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = section === item.id;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => setSection(item.id)}
                className={`relative flex items-center gap-1.5 px-3.5 py-2.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all duration-200 ${
                  isActive
                    ? item.danger
                      ? "bg-error-500/10 text-error-400"
                      : "bg-brand-500/10 text-brand-400 shadow-brand-glow-sm"
                    : "text-surface-400 hover:text-surface-200 hover:bg-surface-800/50"
                }`}
              >
                <Icon className="size-3.5" />
                {item.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Desktop: sidebar + content */}
      <div className="flex gap-6">
        {/* Sidebar nav */}
        <nav className="hidden lg:block w-56 shrink-0 border-r border-surface-700/30">
          <div className="sticky top-6 space-y-1 pr-4">
            {visibleNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = section === item.id;
              return (
                <div key={item.id}>
                  {item.danger && <div className="my-2 border-t border-separator" />}
                  <button
                    type="button"
                    onClick={() => setSection(item.id)}
                    className={`group relative w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-200 ${
                      isActive
                        ? item.danger
                          ? "bg-error-500/10 text-white"
                          : "bg-brand-500/8 text-white"
                        : item.danger
                          ? "text-error-400/60 hover:text-error-400 hover:bg-error-500/5"
                          : "text-surface-400 hover:text-surface-200 hover:bg-surface-800/40"
                    }`}
                  >
                    <div
                      className={`flex items-center justify-center size-8 rounded-lg transition-all duration-200 shrink-0 ${
                        isActive
                          ? item.danger
                            ? "bg-error-500/15 text-error-400"
                            : "bg-brand-500/15 text-brand-400"
                          : item.danger
                            ? "bg-error-500/10 text-error-400/60 group-hover:text-error-400"
                            : "bg-surface-800/50 text-surface-500 group-hover:text-surface-300 group-hover:bg-surface-800"
                      }`}
                    >
                      <Icon className="size-4" />
                    </div>
                    <span className={`text-sm font-semibold ${isActive ? "text-white" : ""}`}>
                      {item.label}
                    </span>
                  </button>
                </div>
              );
            })}
          </div>
        </nav>

        {/* Content panel */}
        <div className="flex-1 min-w-0">
          <SettingsSection
            title={sectionTitles[section]}
            icon={navItems.find((n) => n.id === section)?.icon ?? Settings}
            danger={section === "danger"}
            action={
              <button
                type="button"
                onClick={() => setHelpSection(section)}
                className={`p-1.5 rounded-md text-surface-500 transition-colors ${
                  section === "danger"
                    ? "hover:text-error-400 hover:bg-error-500/10"
                    : "hover:text-brand-400 hover:bg-surface-700/40"
                }`}
                aria-label={`Help for ${sectionTitles[section]}`}
              >
                <HelpCircle className="size-4" />
              </button>
            }
          >
            <SectionContent
              section={section}
              form={form}
              club={club}
              slug={slug}
              isOwner={isOwner || isPlatformAdmin}
              isAdmin={isAdmin}
              canManageInvites={canManageInvites}
              myRole={myRole}
            />
          </SettingsSection>
        </div>
      </div>

      {/* Documentation drawer */}
      <Drawer open={helpSection !== null} onClose={() => setHelpSection(null)}>
        <DrawerHeader subtitle={helpSection ? sectionTitles[helpSection] : undefined}>
          Documentation
        </DrawerHeader>
        <DrawerBody>{helpSection && sectionDocs[helpSection]}</DrawerBody>
      </Drawer>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section content renderer
// ---------------------------------------------------------------------------

function SectionContent({
  section,
  form,
  club,
  slug,
  isOwner,
  isAdmin,
  canManageInvites,
  myRole,
}: {
  section: SectionId;
  form: ClubSettingsForm;
  club: NonNullable<RouterOutputs["club"]["get"]>;
  slug: string;
  isOwner: boolean;
  isAdmin: boolean;
  canManageInvites: boolean;
  myRole: string;
}) {
  switch (section) {
    case "general":
      return <GeneralSection form={form} club={club} />;
    case "social":
      return <SocialLinksSection form={form} />;
    case "discord":
      return (
        <DiscordSection
          clubId={club.id}
          guildId={club.discordGuildId}
          categoryId={club.discordCategoryId}
          disputeWebhookChannelId={club.disputeWebhookChannelId}
          channelMode={club.discordChannelMode}
          moderatorRoleIds={club.discordModeratorRoleIds}
          isOwner={isOwner}
          slug={slug}
        />
      );
    case "invitations":
      return <InvitationsSection clubId={club.id} canManageInvites={canManageInvites} />;
    case "games":
      return <GamesSection clubId={club.id} />;
    case "members":
      return <MembersSection clubId={club.id} clubSlug={slug} isOwner={isOwner} myRole={myRole} />;
    case "applications":
      return <ApplicationsSection clubId={club.id} isAdmin={isAdmin} />;
    case "billing":
      return <BillingSection clubId={club.id} slug={slug} />;
    case "danger":
      return <DangerSection clubId={club.id} clubName={club.name} />;
    default:
      return null;
  }
}
